import React, { PropsWithChildren } from 'react';
import { style, classes } from 'typestyle';
import { NestedCSSProperties } from 'typestyle/lib/types';

interface Props {
  /** Controls the fade-in/out transition. Keep the component mounted for smooth animation. */
  show: boolean;
  onClose: () => void;
  /**
   * When provided, the component renders a managed card with a styled title bar and close button.
   * When omitted, children are passed through directly (useful when the child renders its own card,
   * e.g. the CollectOverlay where AddTo/CreateCollection handle their own card layout).
   */
  title?: React.ReactNode;
  /**
   * Overrides the default card width ('min(600px, 92vw)').
   * Applied as inline style so it always takes precedence over the base card class.
   */
  width?: string;
  /** Extra typestyle class applied to the card div. */
  cardClassName?: string;
  /** Overrides the backdrop z-index (default: 1000). Useful for stacked overlays. */
  zIndex?: number;
}

export function PopupOverlay({
  show,
  onClose,
  title,
  width,
  cardClassName,
  zIndex,
  children,
}: PropsWithChildren<Props>) {
  const backdropStyle = zIndex !== undefined ? { zIndex } : undefined;
  return (
    <div className={backdropClass} data-show={show} onClick={onClose} style={backdropStyle}>
      {title !== undefined ? (
        <div
          className={classes(cardClass, cardClassName)}
          style={width !== undefined ? { width } : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={titleClass}>
            {title}
            <div className={closeClass} onClick={onClose}>
              <i className="material-icons notranslate">close</i>
            </div>
          </div>
          {children}
        </div>
      ) : (
        <div onClick={(e) => e.stopPropagation()}>{children}</div>
      )}
    </div>
  );
}

/**
 * Shared card visual identity — import this in other style files to keep the look in sync
 * without duplicating the values.
 */
export const POPUP_CARD_STYLES: NestedCSSProperties = {
  backgroundColor: '#1f1f1f',
  borderRadius: 20,
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 30px 60px rgba(0,0,0,0.45)',
  overflow: 'hidden',
  position: 'relative',
  color: '#fff',
};

/** Shared backdrop style object — available for special cases that can't use PopupOverlay directly. */
export const POPUP_BACKDROP_STYLES: NestedCSSProperties = {
  zIndex: 1000,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.55)',
  backdropFilter: 'blur(12px)',
  '-webkit-backdrop-filter': 'blur(12px)',
  visibility: 'hidden',
  opacity: 0,
  transition: 'opacity 0.3s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  $nest: {
    '&[data-show="true"]': {
      visibility: 'visible',
      opacity: 1,
    },
  },
};

const backdropClass = style(POPUP_BACKDROP_STYLES);

/** Default managed card: column layout, 600px wide. Callers can override width via the prop. */
const cardClass = style({
  ...POPUP_CARD_STYLES,
  display: 'flex',
  flexDirection: 'column',
  width: 'min(600px, 92vw)',
  padding: '24px',
});

const titleClass = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 20,
  fontWeight: 600,
  marginBottom: 10,
});

const closeClass = style({
  color: '#c2c2c2',
  cursor: 'pointer',
  $nest: { '&:hover': { color: '#fff' } },
});
